import { useAuth } from "react-oidc-context";
import { Button, Dialog, Skeleton } from "@nexus/ui";
import { useAccount } from "@/hooks/useAccount";
import { useTenants } from "@/hooks/useTenants";
import { getDisplayName, getInitials } from "@/lib/userDisplay";

function readable(value: string) {
  return value.replace(/[-_:]/g, " ").replace(/\b\w/g, (letter) => letter.toUpperCase());
}

export function ProfileModal({ onClose }: { onClose: () => void }) {
  const auth = useAuth();
  const account = useAccount();
  const tenants = useTenants();
  const identity = auth.user?.profile;
  const name = getDisplayName(identity);
  const initials = getInitials(identity);
  const accessibleTenants = tenants.data?.map((tenant) => tenant.tenant_label) ?? [];
  const allTenants =
    !account.data?.data_limited_to_scopes &&
    account.data?.scopes.some((scope) => scope.startsWith("global:"));
  const access = allTenants
    ? ["All tenants"]
    : accessibleTenants.length
      ? accessibleTenants
      : (account.data?.scopes.map(readable) ?? []);

  return (
    <Dialog
      open
      title="Profile"
      onClose={onClose}
      closeLabel="Close profile"
      size="sm"
      footer={
        <Button variant="secondary" onClick={onClose}>
          Close
        </Button>
      }
    >
      <div className="space-y-5">
        <section
          aria-label="Identity"
          className="flex min-w-0 items-center gap-4 rounded-panel border border-border bg-surface-elevated/50 p-4"
        >
          <div
            className="grid size-14 shrink-0 place-items-center rounded-2xl border border-brand-cyan/20 bg-brand-cyan/10 text-lg font-semibold text-brand-cyan"
            aria-label={`Initials: ${initials}`}
          >
            {initials}
          </div>
          <div className="min-w-0">
            <h2 className="truncate text-lg font-semibold tracking-tight text-primary" title={name}>
              {name}
            </h2>
            <p className="mt-0.5 break-all text-sm text-secondary">
              {account.data?.email ?? identity?.email}
            </p>
          </div>
        </section>

        {account.isPending && (
          <div
            role="status"
            aria-label="Loading profile"
            className="space-y-3 rounded-panel border border-border p-4"
          >
            <Skeleton className="h-3.5 w-1/3" />
            <Skeleton className="h-3.5 w-2/3" />
            <Skeleton className="h-3.5 w-1/2" />
          </div>
        )}
        {account.isError && (
          <div role="alert" className="flex flex-wrap items-center gap-2 text-sm text-secondary">
            Profile unavailable.
            <Button size="sm" variant="secondary" onClick={() => void account.refetch()}>
              Retry
            </Button>
          </div>
        )}
        {account.data && (
          <dl className="divide-y divide-border overflow-hidden rounded-panel border border-border">
            <div className="px-4 py-3.5">
              <dt className="text-xs font-medium text-muted">Product profile</dt>
              <dd className="mt-1 text-sm font-medium text-primary">
                {readable(account.data.profile)}
              </dd>
            </div>
            <div className="px-4 py-3.5">
              <dt className="text-xs font-medium text-muted">Data access</dt>
              <dd className="mt-2 flex flex-wrap gap-1.5">
                {!allTenants && !accessibleTenants.length && tenants.isPending ? (
                  [0, 1].map((index) => <Skeleton key={index} className="h-6 w-24 rounded-md" />)
                ) : access.length ? (
                  access.map((label) => (
                    <span
                      key={label}
                      className="rounded-md border border-border bg-surface-elevated px-2.5 py-1 text-xs text-secondary"
                    >
                      {label}
                    </span>
                  ))
                ) : (
                  <span className="text-sm text-secondary">No tenant access assigned</span>
                )}
              </dd>
            </div>
          </dl>
        )}
      </div>
    </Dialog>
  );
}
